/**
 * The patient calendar, behind a button.
 *
 * Pete, 2026-09-21: the calendar becomes a generic view of this patient's past
 * and upcoming visits, reached from a calendar icon beside **Visit timeline**
 * and beside **Upcoming Visits** — two buttons, the same look, the same action,
 * because the calendar is the long view of both of those lists and a reader
 * looking at either should be able to reach it without scrolling.
 *
 * It is a dialog rather than a card on the page: a certification period of week
 * rows is a thing you open when you want the shape of the episode, not a thing
 * to scroll past on the way to the next section.
 *
 * The button renders only for a patient the demo fixtures know, the same rule
 * `HomeHealthPatientSection` follows, and nothing about the product's own page
 * changes when the demo layer is off.
 */
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { useOrganization } from "~/providers/GraphqlProvider";
import { isHomeHealthDemoEnabled } from "../../demoEnabled";
import { useDemoState } from "../../store/useDemoStore";
import { EpisodeCalendar } from "./EpisodeCalendar";
import { selectEpisodeFor } from "./EpisodePlanSection";
import { patientRefForPage } from "./HomeHealthPatientSection";

export function HomeHealthCalendarButton({ patientId, patientName }: { patientId?: number; patientName?: { firstName?: string | null; lastName?: string | null } }) {
  const { organizationName } = useOrganization();
  const state = useDemoState();
  const [open, setOpen] = useState(false);
  const patientRef = patientRefForPage(state, patientId, patientName);
  const episode = useMemo(() => (patientRef ? selectEpisodeFor(state, patientRef) : undefined), [state, patientRef]);
  const visits = useMemo(
    () => (episode ? Object.values(state.planVisits).filter((v) => v.episodeId === episode.id && v.status !== "canceled").sort((a, b) => a.targetDate.localeCompare(b.targetDate)) : []),
    [state.planVisits, episode],
  );
  if (!isHomeHealthDemoEnabled(organizationName) || !episode) return null;

  return (
    <>
      <Tooltip title="Calendar">
        <IconButton size="small" aria-label="Calendar" onClick={() => setOpen(true)} data-home-health-calendar-button>
          <CalendarMonthIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && (
        <Dialog open onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            Calendar
            <IconButton size="small" aria-label="Close" onClick={() => setOpen(false)} sx={{ ml: "auto" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <EpisodeCalendar episode={episode} visits={visits} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
